/* eslint-disable camelcase */
// User-facing strings + enum display labels for the Message Details drawer.
// Centralized so copy edits don't require touching the components themselves.

import {
  CustomerState,
  LifecycleStage,
  MessageType,
  RuntimeContextToneOfVoice,
} from './types';

export const displayCustomerState: Record<CustomerState, string> = {
  prospective_customer: 'Prospective Customer',
  one_time_purchaser: 'One-Time Purchaser',
  repeat_customer: 'Repeat Customer',
};

export const displayLifecycleStage: Record<LifecycleStage, string> = {
  acquisition: 'Acquisition',
  interest: 'Interest',
  consideration: 'Consideration',
  conversion: 'Conversion',
  post_purchase: 'Post-Purchase',
  advocacy_loyalty: 'Advocacy & Loyalty',
  reactivation: 'Reactivation',
};

export const displayMessageType: Record<MessageType, string> = {
  engagement: 'Engagement',
  brand_inspiration: 'Brand Inspiration',
  product_education: 'Product Education',
  conversion: 'Conversion',
  transactional: 'Transactional',
};

// The four urgency-lever flags, in Brand Guidelines display order. Each key
// indexes MessageDetails; a flag renders only when true (the blueprint used
// the lever and the shop's Brand Guidelines toggle allowed it at generation
// time). Labels mirror the Brand Guidelines urgency advanced settings.
export const URGENCY_LEVERS: ReadonlyArray<{
  key: 'price_increase' | 'scarcity' | 'timing_pressure' | 'competition';
  label: string;
}> = [
  { key: 'price_increase', label: 'Price Increase' },
  { key: 'scarcity', label: 'Scarcity' },
  { key: 'timing_pressure', label: 'Timing Pressure' },
  { key: 'competition', label: 'Competition' },
];

export const UNSET = '—';
export const UNSPECIFIED_LABEL = 'Unspecified';

// Drawer header title, matching the Figma design (title-case).
export const DRAWER_TITLE = 'Message Details';

export const SECTION_TITLES = {
  overview: 'Overview',
  messageInputs: 'Message Inputs',
  subscriberDetails: 'Subscriber Details',
} as const;

export const ROW_LABELS = {
  customerStatus: 'Customer Status',
  lifecycleStage: 'Lifecycle Stage',
  urgencyLevers: 'Urgency Levers',
  tactic: 'Tactic',
  offer: 'Offer',
  content: 'Content',
  product: 'Product',
  toneOfVoice: 'Tone of Voice',
  name: 'Name',
  phoneNumber: 'Phone Number',
  aiEligible: 'AI Eligible',
} as const;

export const IDENTITY_COPY = {
  shopperAutomation: 'Shopper Automation',
} as const;

// Message Inputs placeholder card, shown in place of the input rows when the
// details snapshot is unavailable. "empty" (404) is calm and permanent-safe —
// older messages predate snapshots and will never get one — while "error"
// covers transient failures and pairs with a Retry affordance.
export const MESSAGE_INPUTS_UNAVAILABLE = {
  empty: "Generation details aren't available for this message.",
  error: "Couldn't load message details.",
  retry: 'Retry',
  openEditor: 'Open contract editor',
} as const;

// Tone-of-voice axes for the Subscriber Details section. Each `key` indexes
// RuntimeContextToneOfVoice so downstream rendering stays type-safe. Order
// and labels mirror the Brand Guidelines 2.0 Tone of Voice sliders
// (BrandingFormFieldsTone): product_lifestyle is the legacy axis Brand
// Guidelines 2.0 hides, so the drawer omits it even though the API still
// sends a value. Axes only render when the API sends a value, so
// relaxed_urgent stays hidden until the details endpoint serves it.
export const TONE_AXES: ReadonlyArray<{
  key: keyof RuntimeContextToneOfVoice;
  left: string;
  right: string;
}> = [
  { key: 'casual_formal', left: 'Casual', right: 'Formal' },
  { key: 'gentle_bold', left: 'Gentle', right: 'Bold' },
  { key: 'concise_chatty', left: 'Concise', right: 'Chatty' },
  { key: 'relaxed_urgent', left: 'Relaxed', right: 'Urgent' },
];
